<template>
	<v-layout justify-center align-center>
		<v-card class="card">
			<v-alert v-if="errorMessage" type="error" class="error">{{ errorMessage }}</v-alert>
			<v-progress-circular class="loading" indeterminate v-else-if="loading"></v-progress-circular>
			<template v-else>
				<v-card-text>
					<v-form>
						<div class="headline"><v-icon>person</v-icon>
							For which <i>student</i> are you designing a page?
						</div>

						<v-row>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="firstName"
									label="First Name"
									:error-messages="firstNameErrors"
									class="required"
									name="firstName"
									@keyup.enter.native="start"/>

							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="lastName"
									label="Last Name"
									:error-messages="lastNameErrors"
									class="required"
									name="lastName"
									@keyup.enter.native="start"/>
							</v-col>

							<v-col cols="12" md="6">
								<v-autocomplete
									v-model="teacher"
									:items="teacherOptions"
									label="Teacher/Home Room"
									:error-messages="teacherErrors"
									class="required"
									auto-select-first
									name="teacher"
									v-if="teacherOptions" />

								<v-text-field
									v-model="teacher"
									label="Teacher/Home Room"
									:error-messages="teacherErrors"
									class="required"
									name="teacher"
									@keyup.enter.native="start"
									v-else />
							</v-col>

							<v-col cols="12" md="6">
								<v-autocomplete
									v-model="grade"
									:items="gradeOptions"
									label="Grade"
									:error-messages="gradeErrors"
									class="required"
									name="grade"
									v-if="gradeOptions" />

								<v-text-field
									v-model="grade"
									label="Grade"
									:error-messages="gradeErrors"
									class="required"
									name="grade"
									@keyup.enter.native="start"
									v-else />
							</v-col>
						</v-row>

						<div class="headline"><v-icon>email</v-icon> Where can we reach you?</div>
						
						<v-text-field label="Email" type="email" v-model="email" name="email" :error-messages="emailErrors" @keyup.enter.native="start"/>
					</v-form>
				</v-card-text>

				<v-alert v-if="saveErrorMessage" type="error" class="error">{{ saveErrorMessage }}</v-alert>
				<v-alert v-else type="info" class="info">
					You will receive an email invite from Captura Yearbooks to design your part of the yearbook.
					<br/>
					Fill your page with pictures of your kids!
				</v-alert>

				<v-card-actions>
					<v-spacer />
					<v-btn color="primary" @click="start">Create Account</v-btn>
					<v-spacer />
				</v-card-actions>
			</template>
		</v-card>
	</v-layout>
</template>

<script>
import { required, minLength, email } from 'vuelidate/lib/validators';
import { client } from '../../PlicCanvas/vue/utils/axios';

export default {
	data() {
		return {
			firstName: '',
			lastName: '',
			teacher: '',
			teacherOptions: null,
			grade: '',
			gradeOptions: null,
			email: '',
			errorMessage: null,
			loading: false,
			saveErrorMessage: null,
			projectId: this.$route.params.id
		};
	},
	validations: {
		firstName: { required, minLength: minLength(2) },
		lastName: { required, minLength: minLength(2) },
		teacher: { required, minLength: minLength(2) },
		grade: { required },
		email: { required, email }
	},
	computed: {
		firstNameErrors() {
			const errors = [];
			if(this.$v.firstName.$dirty) {
				this.$v.firstName.required || errors.push('First Name is required');
				this.$v.firstName.minLength || errors.push('First Name must be at least 2 characters long');
			}

			return errors;
		},
		lastNameErrors() {
			const errors = [];
			if(this.$v.lastName.$dirty) {
				this.$v.lastName.required || errors.push('Last Name is required');
				this.$v.lastName.minLength || errors.push('Last Name must be at least 2 characters long');
			}

			return errors;
		},
		teacherErrors() {
			const errors = [];
			if(this.$v.teacher.$dirty) {
				this.$v.teacher.required || errors.push('Teacher is required');
				this.$v.teacher.minLength || errors.push('Teacher must be at least 3 characters long');
			}

			return errors;
		},
		gradeErrors() {
			const errors = [];
			if(this.$v.grade.$dirty) {
				this.$v.grade.required || errors.push('Grade is required');
			}

			return errors;
		},
		emailErrors() {
			const errors = [];
			if(this.$v.email.$dirty) {
				this.$v.email.required || errors.push('Email is required');
				this.$v.email.email || errors.push('Email is not a valid email');
			}

			return errors;
		}
	},
	methods: {
		start() {
			this.$v.$touch();
			if(this.$v.$error) {
				return;
			}

			this.loading = true;
			this.saveErrorMessage = null;
			client.post('/api/v1/content-page-sign-up', {
				email: this.email,
				firstName: this.firstName,
				lastName: this.lastName,
				teacher: this.teacher,
				grade: this.grade,
				projectId: this.projectId
			}).then(() => {
				this.$router.push('/guest/content-pages/complete');
			}).catch((error) => {
				this.loading = false;
				console.error(error);
				if(error.response && error.response.data && error.response.data.reason) {
					this.saveErrorMessage = error.response.data.reason;
				} else {
					this.saveErrorMessage = error;
				}
			});
		}
	},
	mounted() {
		client.get(`/api/v1/content-page-sign-up?projectId=${this.projectId}`).then((response) => {
			if(response.data.subjectFieldValues?.Teacher && Object.keys(response.data.subjectFieldValues.Teacher).length >= 3) {
				let values = [];
				for(let id in response.data.subjectFieldValues.Teacher) {
					values.push(id);
				}
				values.sort((a, b) => {
					return a.toLowerCase().localeCompare(b.toLowerCase());
				});
				this.teacherOptions = values;
			}
			if(response.data.subjectFieldValues?.Grade && Object.keys(response.data.subjectFieldValues.Grade).length >= 3) {
				let values = [];
				for(let id in response.data.subjectFieldValues.Grade) {
					values.push(id);
				}
				values.sort((a, b) => {
					return a.toLowerCase().localeCompare(b.toLowerCase());
				});
				this.gradeOptions = values;
			}
		}).catch((error) => {
			console.error(error);
			if(error.response && error.response.data && error.response.data.reason) {
				this.errorMessage = error.response.data.reason;
			} else {
				this.errorMessage = error;
			}
		});
	}
}
</script>

<style scoped>
	.subtitle {
		text-align: left;
	}

	.card {
		min-width: 50vw;
	}

	.info {
		margin-left: 1em;
		margin-right: 1em;
	}

	.error {
		margin-bottom: 0;
	}
</style>